<script setup lang="ts">
import { CirclePlus, X } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';

import type { Selection } from '@/types/configuration';
import type { Product } from '@/types/products';

import { useEditor } from '@/composables/useEditor';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { useProductsStore } from '@/stores/useProductsStore';

import TheCard from '../mode-list/TheCard.vue';

defineProps({
  allowDelete: {
    type: Boolean,
    default: false,
  },
});
const emits = defineEmits(['configure', 'delete-item']);

const { configToFilter } = useEditor();
const globalConfigurationStore = useGlobalConfigurationStore();
const { globalConfiguration } = storeToRefs(globalConfigurationStore);
const { getProductsList, formatToProducts } = useProductsStore();

const prefixUrl = computed(() => globalConfiguration.value?.prefixUrl ?? null);
const products = ref(new Array<Product>());

const config = defineModel<Selection>('config', { required: true });

function deleteItem(item: Product) {
  config.value = {
    ...config.value,
    products: config.value.products.filter(it => it !== item.id),
  };
}

async function update() {
  try {
    const data = (await getProductsList({
      size: 20,
      metrics: [
        {
          type: 'taxonomyCount',
          taxonomyTypes: [
            'TYPES',
            'THEMES',
            'PLACES',
          ],
        },
      ],
      filters: configToFilter(config.value),
      sorts: [],
      properties: [],
      selections: [],
    })).data;
    products.value = formatToProducts(data);
  }
  catch (e) {
    console.error(`Failed to get products list for selection preview: ${e}`);
  }
}

onMounted(() => {
  update();
});

watch(config, update, { deep: true });
</script>

<template>
  <div class="selection-preview">
    <div
      role="button" tabindex="0" class="selection selection-configure" @click="emits('configure')"
      @keydown.enter="emits('configure')"
    >
      <div class="icon">
        <slot name="configure-icon">
          <CirclePlus />
        </slot>
      </div>
      <span class="title">
        <slot name="configure-title">{{ $t('editor.add_product') }}</slot>
      </span>
      <span class="subtitle">
        <slot name="configure-subtitle" />
      </span>
    </div>
    <div class="cards-container">
      <div v-for="product in products" :key="product.id" class="card-item">
        <TheCard
          class="card" style="pointer-events: none; height: 99%" :product="product" :responsive-row="false"
          :show-favorite="false" :prefix-url="prefixUrl"
        />
        <div
          v-if="allowDelete" role="button" tabindex="0" class="delete-icon" @click="deleteItem(product)"
          @keydown.enter="deleteItem(product)"
        >
          <X />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="postcss">
.selection-preview {
  display: flex;
  gap: 1rem;
  flex: 1;
  min-height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
}

.selection {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-width: 280px;
  background: #f8f9fa;
  border-radius: 8px;
}

.selection-configure {
  background: #eaf4fe;
  color: var(--yb-color-primary);
  cursor: pointer;
  transition: ease-in-out background 100ms;

  .icon > * {
    color: var(--yb-color-primary);
    --size: 3rem;
    width: var(--size);
    height: var(--size);
  }

  .title {
    font-size: 1.2rem;
    font-weight: 700;
    margin-top: 0.5rem;
  }

  .subtitle {
    font-size: 0.8rem;
    margin-top: 0.1rem;
  }
}

.selection-configure:hover {
  background: #dcebf7;
}

.cards-container {
  display: flex;
  gap: 0.75rem;
  max-width: 100%;
  overflow-x: auto;
}

.card-item {
  position: relative;
  max-width: 300px;
  min-width: 300px;
}

.delete-icon {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  color: #484848;
  cursor: pointer;
}
</style>
